import { useEffect, useRef } from 'react';
import { clsx } from 'clsx';

interface ResumeOrStartFreshDialogProps {
  isOpen: boolean;
  onResume: () => void;
  onStartFresh: () => void;
  characterName: string;
  raceName?: string;
  className?: string;
}

/**
 * Dialog shown when navigating to /create with an existing draft in progress.
 * Offers the user a choice to resume their draft or start fresh.
 */
export function ResumeOrStartFreshDialog({
  isOpen,
  onResume,
  onStartFresh,
  characterName,
  raceName,
  className,
}: ResumeOrStartFreshDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close on escape key (resume is the safe default)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onResume();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onResume]);

  // Close on click outside (resume is the safe default)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onResume();
      }
    };
    if (isOpen) {
      const timer = setTimeout(() => {
        window.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpen, onResume]);

  if (!isOpen) return null;

  const displayName = characterName.trim() || 'Unnamed Character';
  const summary = [raceName, className].filter(Boolean).join(' ');

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm animate-fade-in"
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          className={clsx(
            'w-full max-w-md bg-background-card rounded-xl border border-border-default',
            'shadow-2xl animate-scale-in'
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="resume-dialog-title"
          aria-describedby="resume-dialog-description"
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <ResumeIcon className="w-5 h-5 text-amber-400" />
              </div>
              <h2
                id="resume-dialog-title"
                className="text-lg font-semibold text-text-primary"
              >
                Unfinished Character Found
              </h2>
            </div>
            <p id="resume-dialog-description" className="text-text-secondary">
              You have an unfinished character:{' '}
              <span className="font-medium text-text-primary">{displayName}</span>
              {summary && (
                <span className="text-text-muted"> ({summary})</span>
              )}
              . Would you like to continue where you left off?
            </p>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex gap-3">
            <button
              type="button"
              onClick={onStartFresh}
              className="flex-1 px-4 py-2.5 rounded-lg border border-border-default text-text-secondary font-medium hover:bg-background-elevated transition-colors"
            >
              Start Fresh
            </button>
            <button
              type="button"
              onClick={onResume}
              className="flex-1 px-4 py-2.5 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors"
            >
              Resume Editing
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function ResumeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}
